import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Form as FinalForm, Field } from 'react-final-form';
import { Form, Modal, Grid } from 'semantic-ui-react';
import get from 'lodash.get';

// Types
import { AppState } from 'services/types';

// Form
import {
    normalizers,
    validators,
    RenderCheckbox,
    RenderField,
    RenderDatePicker,
    RenderDropdown
} from 'forms';
import { timesheetEntryDecorator } from 'forms/decorators';

// Global Components
// @ts-ignore
import EntityOptions from 'components/EntityOptions';
// @ts-ignore
import EntityTypeName from 'components/EntityTypeName';
// @ts-ignore
import PersonOptions from 'components/PersonOptions';
// @ts-ignore
import SelectedTimesheetPersonExistsGate from 'components/SelectedTimesheetPersonExistsGate';
// @ts-ignore
import WorkTypeOptions from 'components/WorkTypeOptions';
import FieldValues from 'components/FieldValues';
import HarvestEventDocumentsRequest from 'components/HarvestEventDocumentsRequest';
import IsHarvestSchemaWorkTypeGate from 'components/IsHarvestSchemaWorkTypeGate';

// Global Selectors
import {
    makeSelectTimesheetValue
    // @ts-ignore
} from 'services/timesheet/selectors';
import { selectBinTotalsByPersonAndDate } from 'services/harvestEventDocuments/selectors';

// Local Components
import SubmittingSection from 'components/SubmissionSection';
import LunchBreakOptions from '../LunchBreakOptions';
// @ts-ignore
import TimeOptions from '../TimeOptions';
import HarvestEventInspectionDocumentsMessage from './components/HarvestEventInspectionDocumentsMessage';

// Local Selectors
// @ts-ignore
import { selectInitialValues } from './selectors';
import PaidBreakOptions from '../PaidBreakOptions';

// Local Utils
import { extrapolateTimesheetEntries } from './utils';
import { timesheetTimesheetEntryFormValidate } from './validate';

// Validators
const {
    composeValidators,
    isRequired,
    isPositiveNumber,
    maxValue1000,
    maxValue10000,
    hasLength,
    isNotBeforeTwoYearsAgo
} = validators;

// Normalizers
const { toNumberOrNull, toNull, toArrayIfFalsy } = normalizers;

export interface OwnProps {
    title: string;
    onClose: () => void;
    onSubmit: (timesheetEntries: any) => void;
    defaultValues: any;
}

interface ConnectedProps {
    initialValues?: {};
    timesheetEndDate?: string;
    timesheetStartDate?: string;
    binTotalsByPersonAndDate: any;
}

type Props = OwnProps & ConnectedProps;

export class TimesheetTimesheetEntryFormComponent extends React.Component<Props> {
    shouldComponentUpdate(): boolean {
        return false;
    }

    handleSubmit = (values: any): void => {
        const { onClose, onSubmit, binTotalsByPersonAndDate } = this.props;

        const { personIds, startDate, endDate, pickTypeId, ...data } = values;
        const units = data.units || undefined;

        const timesheetEntries = extrapolateTimesheetEntries(
            personIds,
            startDate,
            endDate,
            {
                ...data,
                units,
                rate: data.rate || undefined,
                paidBreakMinutes: data.paidBreakMinutes || undefined
            }
        );

        const timesheetEntriesWithUnits = timesheetEntries.map(
            (timesheetEntry: any): any => {
                const { date, personId } = timesheetEntry;
                return {
                    ...timesheetEntry,
                    units: get(
                        binTotalsByPersonAndDate,
                        [date, personId],
                        units
                    )
                };
            }
        );

        onSubmit(timesheetEntriesWithUnits);
        onClose();
    };

    render(): React.ReactElement {
        const {
            title,
            onClose,
            initialValues,
            timesheetEndDate,
            timesheetStartDate
        } = this.props;

        return (
            <React.Fragment>
                <Modal.Header>{title}</Modal.Header>
                <Modal.Content className="constrain-width">
                    <FinalForm
                        onSubmit={this.handleSubmit}
                        decorators={[timesheetEntryDecorator]}
                        initialValues={initialValues}
                        subscription={{}}
                        validate={timesheetTimesheetEntryFormValidate}
                        render={(props: any): React.ReactElement => {
                            const { handleSubmit } = props;
                            return (
                                <Form
                                    autoComplete="off"
                                    onSubmit={handleSubmit}
                                >
                                    <React.Fragment>
                                        <Grid className="column-padding">
                                            <Grid.Row>
                                                <SelectedTimesheetPersonExistsGate
                                                    FallbackComponent={
                                                        <Grid.Column width={16}>
                                                            <PersonOptions>
                                                                {(
                                                                    options: any
                                                                ): React.ReactElement => (
                                                                    <Field
                                                                        name="personIds"
                                                                        label="People"
                                                                        placeholder="Add people"
                                                                        validate={
                                                                            hasLength
                                                                        }
                                                                        parse={
                                                                            toArrayIfFalsy
                                                                        }
                                                                        component={
                                                                            RenderDropdown as any
                                                                        }
                                                                        subscription={{
                                                                            value: true,
                                                                            touched:
                                                                                true,
                                                                            error: true
                                                                        }}
                                                                        options={
                                                                            options
                                                                        }
                                                                        multiple
                                                                        clearable
                                                                        fluid
                                                                        search
                                                                        selection
                                                                        closeOnChange
                                                                    />
                                                                )}
                                                            </PersonOptions>
                                                        </Grid.Column>
                                                    }
                                                />
                                                <Grid.Column
                                                    mobile={16}
                                                    tablet={8}
                                                    computer={8}
                                                >
                                                    <Field
                                                        name="endDate"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => (
                                                            <Field
                                                                name="startDate"
                                                                subscription={{
                                                                    value: true,
                                                                    touched:
                                                                        true,
                                                                    error: true
                                                                }}
                                                                validate={composeValidators(
                                                                    isRequired,
                                                                    isNotBeforeTwoYearsAgo
                                                                )}
                                                                component={
                                                                    RenderDatePicker as any
                                                                }
                                                                label="Start Date"
                                                                placeholder="Add start date"
                                                                dateFormat="EEE do MMM"
                                                                selectsStart
                                                                endDate={value}
                                                                minDate={
                                                                    timesheetStartDate
                                                                }
                                                                maxDate={
                                                                    value ||
                                                                    timesheetEndDate
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid.Column>
                                                <Grid.Column
                                                    mobile={16}
                                                    tablet={8}
                                                    computer={8}
                                                >
                                                    <Field
                                                        name="startDate"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => (
                                                            <Field
                                                                name="endDate"
                                                                subscription={{
                                                                    value: true,
                                                                    touched:
                                                                        true,
                                                                    error: true
                                                                }}
                                                                validate={composeValidators(
                                                                    isRequired,
                                                                    isNotBeforeTwoYearsAgo
                                                                )}
                                                                component={
                                                                    RenderDatePicker as any
                                                                }
                                                                label="End Date"
                                                                placeholder="Add end date"
                                                                dateFormat="EEE do MMM"
                                                                selectsEnd
                                                                startDate={
                                                                    value
                                                                }
                                                                minDate={
                                                                    value ||
                                                                    timesheetStartDate
                                                                }
                                                                maxDate={
                                                                    timesheetEndDate
                                                                }
                                                            />
                                                        )}
                                                    </Field>
                                                </Grid.Column>
                                                <Grid.Column
                                                    mobile={16}
                                                    tablet={8}
                                                    computer={8}
                                                >
                                                    <Field
                                                        name="endTime"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => (
                                                            <TimeOptions
                                                                isStart
                                                                endTime={value}
                                                            >
                                                                {(
                                                                    options: any
                                                                ): React.ReactElement => (
                                                                    <Field
                                                                        component={
                                                                            RenderDropdown as any
                                                                        }
                                                                        name="startTime"
                                                                        subscription={{
                                                                            value: true,
                                                                            touched:
                                                                                true,
                                                                            error: true
                                                                        }}
                                                                        options={
                                                                            options
                                                                        }
                                                                        label="Start Time"
                                                                        placeholder="Enter start time"
                                                                        parse={
                                                                            toNull
                                                                        }
                                                                        fluid
                                                                        search
                                                                        selection
                                                                        clearable
                                                                        selectOnBlur={
                                                                            false
                                                                        }
                                                                    />
                                                                )}
                                                            </TimeOptions>
                                                        )}
                                                    </Field>
                                                </Grid.Column>
                                                <Grid.Column
                                                    mobile={16}
                                                    tablet={8}
                                                    computer={8}
                                                >
                                                    <Field
                                                        name="startTime"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => (
                                                            <TimeOptions
                                                                isEnd
                                                                startTime={
                                                                    value
                                                                }
                                                            >
                                                                {(
                                                                    options: any
                                                                ): React.ReactElement => (
                                                                    <Field
                                                                        component={
                                                                            RenderDropdown as any
                                                                        }
                                                                        subscription={{
                                                                            value: true,
                                                                            touched:
                                                                                true,
                                                                            error: true
                                                                        }}
                                                                        options={
                                                                            options
                                                                        }
                                                                        name="endTime"
                                                                        label="End Time"
                                                                        placeholder="Enter end time"
                                                                        parse={
                                                                            toNull
                                                                        }
                                                                        fluid
                                                                        search
                                                                        selection
                                                                        clearable
                                                                        selectOnBlur={
                                                                            false
                                                                        }
                                                                    />
                                                                )}
                                                            </TimeOptions>
                                                        )}
                                                    </Field>
                                                </Grid.Column>
                                                <Grid.Column width={16}>
                                                    <Field
                                                        name="hasEditedUnpaidBreak"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => (
                                                            <LunchBreakOptions>
                                                                {(
                                                                    options: any
                                                                ): React.ReactElement => (
                                                                    <Field
                                                                        options={
                                                                            options
                                                                        }
                                                                        component={
                                                                            RenderDropdown as any
                                                                        }
                                                                        subscription={{
                                                                            value: true,
                                                                            touched:
                                                                                true,
                                                                            error: true
                                                                        }}
                                                                        name="unpaidBreakMinutes"
                                                                        label="Lunch Break (optional)"
                                                                        placeholder="Enter break"
                                                                        parse={
                                                                            toNull
                                                                        }
                                                                        disabled={
                                                                            !value
                                                                        }
                                                                        clearable={
                                                                            value
                                                                        }
                                                                        fluid
                                                                        search
                                                                        selection
                                                                        selectOnBlur={
                                                                            false
                                                                        }
                                                                    />
                                                                )}
                                                            </LunchBreakOptions>
                                                        )}
                                                    </Field>
                                                </Grid.Column>
                                                <Grid.Column
                                                    mobile={16}
                                                    tablet={16}
                                                    computer={16}
                                                >
                                                    <Field
                                                        name="hasEditedUnpaidBreak"
                                                        component={
                                                            RenderCheckbox as any
                                                        }
                                                        label="I want to manually enter the lunch break"
                                                    />
                                                </Grid.Column>
                                            </Grid.Row>
                                            <Grid.Row>
                                                <Grid.Column width={16}>
                                                    <WorkTypeOptions
                                                        useCompositeKey
                                                    >
                                                        <Field
                                                            name="workTypeCompositeKey"
                                                            label="Task"
                                                            placeholder="Add task"
                                                            parse={toNull}
                                                            fluid
                                                            component={
                                                                RenderDropdown as any
                                                            }
                                                            subscription={{
                                                                value: true,
                                                                touched: true,
                                                                error: true
                                                            }}
                                                            search
                                                            selection
                                                            closeOnChange
                                                            upward={false}
                                                            clearable
                                                            selectOnBlur={false}
                                                        />
                                                    </WorkTypeOptions>
                                                </Grid.Column>
                                                <Grid.Column width="16">
                                                    <Field
                                                        name="workTypeCompositeKey"
                                                        subscription={{
                                                            value: true
                                                        }}
                                                    >
                                                        {({
                                                            input: { value }
                                                        }: any): React.ReactElement => {
                                                            const [
                                                                workTypeIdString,
                                                                ,
                                                                workTypeEntityTypeIdString
                                                            ] =
                                                                value.split(
                                                                    '.'
                                                                );
                                                            const workTypeId =
                                                                Number(
                                                                    workTypeIdString
                                                                );
                                                            const workTypeEntityTypeId =
                                                                Number(
                                                                    workTypeEntityTypeIdString
                                                                );

                                                            return (
                                                                <React.Fragment>
                                                                    <EntityTypeName
                                                                        entityTypeId={
                                                                            workTypeEntityTypeId
                                                                        }
                                                                    >
                                                                        {(
                                                                            entityTypeName: string
                                                                        ): React.ReactElement => (
                                                                            <EntityOptions
                                                                                entityTypeId={
                                                                                    workTypeEntityTypeId
                                                                                }
                                                                                useCompositeKey
                                                                            >
                                                                                {(
                                                                                    options: any
                                                                                ): React.ReactElement => (
                                                                                    <Field
                                                                                        component={
                                                                                            RenderDropdown as any
                                                                                        }
                                                                                        subscription={{
                                                                                            value: true,
                                                                                            touched:
                                                                                                true,
                                                                                            error: true
                                                                                        }}
                                                                                        options={
                                                                                            options
                                                                                        }
                                                                                        name="entityCompositeKey"
                                                                                        label={`${
                                                                                            entityTypeName ||
                                                                                            'Area'
                                                                                        } (optional)`}
                                                                                        placeholder={`Add ${
                                                                                            entityTypeName
                                                                                                ? entityTypeName.toLowerCase()
                                                                                                : 'area'
                                                                                        }`}
                                                                                        fluid
                                                                                        search
                                                                                        selection
                                                                                        clearable
                                                                                        selectOnBlur={
                                                                                            false
                                                                                        }
                                                                                    />
                                                                                )}
                                                                            </EntityOptions>
                                                                        )}
                                                                    </EntityTypeName>
                                                                    <IsHarvestSchemaWorkTypeGate
                                                                        workTypeId={
                                                                            workTypeId
                                                                        }
                                                                    >
                                                                        <Grid.Column
                                                                            width={
                                                                                16
                                                                            }
                                                                        >
                                                                            <FieldValues
                                                                                fieldNames={[
                                                                                    'entityCompositeKey',
                                                                                    'startDate',
                                                                                    'endDate',
                                                                                    'personIds'
                                                                                ]}
                                                                            >
                                                                                {({
                                                                                    entityCompositeKey,
                                                                                    startDate,
                                                                                    endDate,
                                                                                    personIds
                                                                                }): React.ReactElement => {
                                                                                    const [
                                                                                        entityIdString
                                                                                    ]: any =
                                                                                        entityCompositeKey
                                                                                            ? entityCompositeKey.split(
                                                                                                  '.'
                                                                                              )
                                                                                            : [];
                                                                                    const entityId =
                                                                                        Number(
                                                                                            entityIdString
                                                                                        );

                                                                                    return (
                                                                                        <React.Fragment>
                                                                                            <HarvestEventDocumentsRequest
                                                                                                entityId={
                                                                                                    entityId
                                                                                                }
                                                                                            />
                                                                                            <HarvestEventInspectionDocumentsMessage
                                                                                                entityId={
                                                                                                    entityId
                                                                                                }
                                                                                                startDate={
                                                                                                    startDate
                                                                                                }
                                                                                                endDate={
                                                                                                    endDate
                                                                                                }
                                                                                                personIds={
                                                                                                    personIds
                                                                                                }
                                                                                            />
                                                                                        </React.Fragment>
                                                                                    );
                                                                                }}
                                                                            </FieldValues>
                                                                        </Grid.Column>
                                                                    </IsHarvestSchemaWorkTypeGate>
                                                                </React.Fragment>
                                                            );
                                                        }}
                                                    </Field>
                                                </Grid.Column>
                                            </Grid.Row>
                                            <Field
                                                name="workTypeCompositeKey"
                                                subscription={{ value: true }}
                                            >
                                                {({
                                                    input: { value }
                                                }: any): React.ReactElement | null => {
                                                    const [
                                                        ,
                                                        isPieceRateString
                                                    ] = value.split('.');
                                                    const isPieceRate =
                                                        isPieceRateString ===
                                                        'true';
                                                    if (!isPieceRate) {
                                                        return null;
                                                    }
                                                    return (
                                                        <Grid.Row>
                                                            <Grid.Column
                                                                mobile={16}
                                                                tablet={8}
                                                                computer={8}
                                                            >
                                                                <Field
                                                                    name="units"
                                                                    component={
                                                                        RenderField as any
                                                                    }
                                                                    subscription={{
                                                                        value: true,
                                                                        touched:
                                                                            true,
                                                                        error: true
                                                                    }}
                                                                    label="Count (optional)"
                                                                    type="number"
                                                                    placeholder="Enter units"
                                                                    parse={
                                                                        toNumberOrNull
                                                                    }
                                                                    validate={composeValidators(
                                                                        isPositiveNumber,
                                                                        maxValue1000
                                                                    )}
                                                                />
                                                            </Grid.Column>
                                                            <Grid.Column
                                                                mobile={16}
                                                                tablet={8}
                                                                computer={8}
                                                            >
                                                                <Field
                                                                    name="rate"
                                                                    component={
                                                                        RenderField as any
                                                                    }
                                                                    subscription={{
                                                                        value: true,
                                                                        touched:
                                                                            true,
                                                                        error: true
                                                                    }}
                                                                    label="Rate (optional)"
                                                                    type="number"
                                                                    placeholder="Enter rate"
                                                                    parse={
                                                                        toNumberOrNull
                                                                    }
                                                                    validate={composeValidators(
                                                                        isPositiveNumber,
                                                                        maxValue10000
                                                                    )}
                                                                />
                                                            </Grid.Column>
                                                            <Grid.Column
                                                                width={16}
                                                            >
                                                                <Field
                                                                    name="hasEditedPaidBreak"
                                                                    subscription={{
                                                                        value: true
                                                                    }}
                                                                >
                                                                    {({
                                                                        input
                                                                    }: any): React.ReactElement => (
                                                                        <PaidBreakOptions>
                                                                            {(
                                                                                options: any
                                                                            ): React.ReactElement => (
                                                                                <Field
                                                                                    name="paidBreakMinutes"
                                                                                    options={
                                                                                        options
                                                                                    }
                                                                                    component={
                                                                                        RenderDropdown as any
                                                                                    }
                                                                                    subscription={{
                                                                                        value: true,
                                                                                        touched:
                                                                                            true,
                                                                                        error: true
                                                                                    }}
                                                                                    label="Paid Break (optional)"
                                                                                    placeholder="Enter paid break"
                                                                                    parse={
                                                                                        toNull
                                                                                    }
                                                                                    disabled={
                                                                                        !input.value
                                                                                    }
                                                                                    clearable={
                                                                                        input.value
                                                                                    }
                                                                                    fluid
                                                                                    search
                                                                                    selection
                                                                                    selectOnBlur={
                                                                                        false
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </PaidBreakOptions>
                                                                    )}
                                                                </Field>
                                                            </Grid.Column>
                                                            <Grid.Column
                                                                mobile={16}
                                                                tablet={16}
                                                                computer={16}
                                                            >
                                                                <Field
                                                                    name="hasEditedPaidBreak"
                                                                    component={
                                                                        RenderCheckbox as any
                                                                    }
                                                                    label="I want to manually enter the paid break"
                                                                />
                                                            </Grid.Column>
                                                        </Grid.Row>
                                                    );
                                                }}
                                            </Field>
                                        </Grid>
                                    </React.Fragment>
                                    <SubmittingSection
                                        normalCancelButtonParam={{
                                            onClose: (): void => {
                                                onClose();
                                            }
                                        }}
                                    />
                                </Form>
                            );
                        }}
                    />
                </Modal.Content>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: selectInitialValues,
    timesheetEndDate: makeSelectTimesheetValue('endDate'),
    timesheetStartDate: makeSelectTimesheetValue('startDate'),
    binTotalsByPersonAndDate: selectBinTotalsByPersonAndDate
});

export default connect(mapStateToProps)(TimesheetTimesheetEntryFormComponent);
